// Considered: AspectCatalogMolecule — not extended directly because a perspective
// isn't a PropertyOption (no `type`/`enumValues`, and grouping is by owning sphere
// type + an in-scope/out-of-scope split PropertyPicker has no concept of). Reuses
// PickerShellMolecule, the chrome the property picker itself was extracted onto,
// so both pickers share one list/group/scope-banner/empty-state implementation.
import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import PickerShellMolecule, { PickerShellBucket } from '../PickerShell/PickerShellMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { getFilterTypeAccentVar, getFilterTypeIconClassName } from '../../../utils/filterTypeVisuals';
import type { PerspectiveCatalogGroup } from '../../../utils/filterPropertyBuilder';
import './PerspectivePickerMolecule.scss';

export interface PerspectivePickerMoleculeProps {
  /** Currently-bound perspective system_name (highlighted), or null. */
  current: string | null;
  /** The grouped catalog to choose from — see `buildPerspectiveCatalog`. */
  groups: PerspectiveCatalogGroup[];
  onPick: (systemName: string) => void;
  /** Renders a cancel affordance below the list when provided. */
  onCancel?: () => void;
  /** True while the perspective catalog is still being resolved. */
  loading?: boolean;
  /** Non-null when the perspective catalog failed to resolve. */
  error?: string | null;
  /** Every option renders `aria-disabled` and ignores picks. */
  disabled?: boolean;
  testIdPrefix?: string;
  className?: string;
}

interface FlatPerspective {
  systemName: string;
  label: string;
  icon?: string;
  groupId: string;
  groupLabel: string;
  groupNote?: string;
  inScope: boolean;
}

// The CONTENT of one option. `PickerShellMolecule` owns the `role="option"` wrapper, its
// `aria-selected` and its activation; an option may hold no interactive descendant, so
// this row carries no button and no handler of its own.
function PerspectivePickerRow({
  perspective,
  selected,
  testIdPrefix,
}: Readonly<{
  perspective: FlatPerspective;
  selected: boolean;
  testIdPrefix: string;
}>) {
  return (
    <div
      className={`perspective-picker-molecule__row${selected ? ' perspective-picker-molecule__row--selected' : ''}${
        perspective.inScope ? '' : ' perspective-picker-molecule__row--out-of-scope'
      }`}
      data-testid={`${testIdPrefix}-${perspective.systemName}`}
      style={{ '--pp-row-accent': getFilterTypeAccentVar('perspective') } as React.CSSProperties}
    >
      <i className={perspective.icon ?? getFilterTypeIconClassName('perspective')} aria-hidden="true" />
      <span className="perspective-picker-molecule__row-label">{perspective.label}</span>
      {selected && <i className="pi pi-check perspective-picker-molecule__row-check" aria-hidden="true" />}
    </div>
  );
}

const PerspectivePickerMolecule: React.FC<PerspectivePickerMoleculeProps> = ({
  current,
  groups,
  onPick,
  onCancel,
  loading = false,
  error = null,
  disabled = false,
  testIdPrefix = 'perspective-picker-option',
  className = '',
}) => {
  const { t } = useTranslation();

  // The shell's banner answers "what am I looking at", the way the aspect picker's
  // does. A catalog is scoped when the view's levels constrain it to specific sphere
  // types — `buildPerspectiveCatalog` marks the groups it can't reach `inScope: false`,
  // so the presence of any such group is what "scoped" means here.
  const scopeLabel = useMemo(() => {
    const typeGroups = groups.filter((g) => g.id !== 'common');
    const inScopeTypeGroups = typeGroups.filter((g) => g.inScope);
    return inScopeTypeGroups.length === typeGroups.length
      ? t('context.viewEditor.atlas.perspectivePicker.unscopedLabel')
      : t('context.viewEditor.atlas.perspectivePicker.scopedLabel', { count: inScopeTypeGroups.length });
  }, [groups, t]);

  const flatItems = useMemo<FlatPerspective[]>(
    () =>
      groups.flatMap((group) =>
        group.perspectives.map((p) => ({
          systemName: p.systemName,
          label: p.label,
          icon: p.icon,
          groupId: group.id,
          groupLabel: group.label,
          groupNote: group.note,
          inScope: group.inScope,
        }))
      ),
    [groups]
  );

  const computeBuckets = useMemo(
    () =>
      (filtered: FlatPerspective[]): PickerShellBucket<FlatPerspective>[] => {
        const order = groups.map((g) => g.id);
        const byGroup = new Map<string, FlatPerspective[]>();
        filtered.forEach((item) => {
          const bucket = byGroup.get(item.groupId) ?? [];
          bucket.push(item);
          byGroup.set(item.groupId, bucket);
        });
        return order
          .filter((id) => byGroup.has(id))
          .map((id) => {
            const items = byGroup.get(id) ?? [];
            const inScope = items[0]?.inScope ?? true;
            return {
              key: id,
              label: items[0]?.groupLabel ?? id,
              // "Inherited by every sphere type" / "Defined on this sphere type". The
              // catalog has always populated this and nothing rendered it, so the two
              // translation keys behind it read as dead — they are not; the picker was
              // simply dropping the field when it flattened groups into buckets.
              note: items[0]?.groupNote,
              badge: inScope ? undefined : (
                <ChipAtom
                  className="perspective-picker-molecule__out-of-scope-badge"
                  label={t('context.viewEditor.atlas.perspectivePicker.outOfScope')}
                />
              ),
              items,
            };
          });
      },
    [groups, t]
  );

  return (
    <PickerShellMolecule<FlatPerspective>
      items={flatItems}
      getSearchText={(p) => `${p.label} ${p.systemName}`}
      getRowKey={(p) => p.systemName}
      renderRow={(p) => (
        <PerspectivePickerRow perspective={p} selected={current === p.systemName} testIdPrefix={testIdPrefix} />
      )}
      onPickItem={(p) => {
        if (!disabled) onPick(p.systemName);
      }}
      isItemSelected={(p) => current === p.systemName}
      isItemDisabled={() => disabled}
      computeBuckets={computeBuckets}
      onCancel={onCancel}
      scopeLabel={scopeLabel}
      loading={loading}
      loadingMessage={t('context.viewEditor.atlas.perspectivePicker.loading')}
      errorMessage={error}
      noMatchesMessage={t('context.viewEditor.atlas.perspectivePicker.noMatches')}
      searchPlaceholder={t('context.viewEditor.atlas.perspectivePicker.searchPlaceholder')}
      cancelLabel={t('context.viewEditor.atlas.perspectivePicker.cancel')}
      testIdPrefix={testIdPrefix}
      className={`perspective-picker-molecule ${className}`.trim()}
    />
  );
};

PerspectivePickerMolecule.displayName = 'PerspectivePickerMolecule';

export default PerspectivePickerMolecule;
